import { useMemo, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import MdxEditorField from "@/components/ui/MdxEditorField";
import { useText } from "@/api/api";
import { resolveScriptScrollAnchor } from "@/services/scriptScrollAnchor";
import { ProjectPanelRoute } from "@/types/routes";
import type { ProjectDetailsProject, ProjectDetailsStoryboardShot } from "@/types/project";
import { useScriptAutoScroll } from "./useScriptAutoScroll";

export interface ScriptTabPanelProps {
  readonly projectId: string | null;
  readonly project: ProjectDetailsProject | null;
  readonly activeShot: ProjectDetailsStoryboardShot | undefined;
  readonly panel: string | undefined;
  readonly active: boolean;
}

function EmptyState({ message, isError = false }: { readonly message: string; readonly isError?: boolean }) {
  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 3 }}>
      <Typography variant="body2" color={isError ? "error" : undefined} sx={{ color: isError ? undefined : "text.secondary", textAlign: "center" }}>
        {message}
      </Typography>
    </Box>
  );
}

const ScriptTabPanel = ({ projectId, project, activeShot, panel, active }: ScriptTabPanelProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const normalizedScriptTextId =
    typeof project?.script_text_id === "string" && project.script_text_id.trim().length > 0 ? project.script_text_id.trim() : null;

  const scriptTextQuery = useText(projectId, normalizedScriptTextId);

  const isOnShotPanel = panel === ProjectPanelRoute.ImageFrame || panel === ProjectPanelRoute.Video;
  const anchor = useMemo(
    () => (isOnShotPanel ? resolveScriptScrollAnchor(project, activeShot) : null),
    [isOnShotPanel, project, activeShot]
  );

  const isHydrating = normalizedScriptTextId !== null && !scriptTextQuery.isSuccess && (scriptTextQuery.isPending || scriptTextQuery.isFetching);

  useScriptAutoScroll({
    containerRef,
    anchor,
    enabled: active,
    contentReady: scriptTextQuery.isSuccess
  });

  if (normalizedScriptTextId === null) {
    return <EmptyState message="No script yet — add one from the Script tool." />;
  }

  if (isHydrating) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (scriptTextQuery.isError) {
    return <EmptyState message="Failed to load script" isError />;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        px: 3,
        py: 2,
        "& .mdx-content": {
          outline: "none",
          color: "text.secondary",
          fontSize: 14,
          lineHeight: "22px"
        }
      }}
    >
      <MdxEditorField
        value={scriptTextQuery.data?.text ?? ""}
        onChange={() => void 0}
        placeholder="No script yet"
        syncKey={`${projectId ?? "no-project"}-${normalizedScriptTextId}`}
        disabled
      />
    </Box>
  );
};

export default ScriptTabPanel;
